import { useCallback, useEffect, useState } from 'react';

import useOnboardingConfig from './useOnboardingConfig';
import { useUserPreferences } from './useUserPreferences';

// Single source of truth for wizard lifecycle; coordinates prefs, config, and once-per-agent auto-open.
export const useOnboardingPresenter = (sourceName?: string) => {
  const {
    preferences,
    isOnboardingComplete,
    shouldShowOnboarding,
    isLoading: isLoadingPreferences,
    saveAndComplete,
    skipOnboarding,
    clearSkip,
  } = useUserPreferences(sourceName);
  const { isActive, questions, isLoading: isLoadingConfig } = useOnboardingConfig(sourceName);

  const [isOpen, setIsOpen] = useState(false);
  const [evaluatedFor, setEvaluatedFor] = useState<string | null>(null);

  const isReady = !!sourceName && !isLoadingPreferences && !isLoadingConfig;
  const shouldAutoOpen = shouldShowOnboarding && isActive && questions.length > 0;
  const isPersonalizeVisible = isReady && isActive && !isOnboardingComplete;

  // Close the wizard whenever the agent changes so the previous agent's
  // currentStep is never rendered against the new agent's questions.
  useEffect(() => {
    setIsOpen(false);
  }, [sourceName]);

  // Evaluate auto-open exactly once per agent, after both prefs and config
  // settle. `evaluatedFor === sourceName` is the idempotency latch — replaces
  // the previous useRef-based gate that could fire unpredictably on extra
  // renders (StrictMode double-effect, query refetch, etc.).
  useEffect(() => {
    if (!isReady) return;
    if (evaluatedFor === sourceName) return;
    setEvaluatedFor(sourceName ?? null);
    if (shouldAutoOpen) setIsOpen(true);
  }, [isReady, sourceName, evaluatedFor, shouldAutoOpen]);

  const open = useCallback(() => {
    clearSkip();
    setIsOpen(true);
  }, [clearSkip]);

  const close = useCallback(() => setIsOpen(false), []);

  const skip = useCallback(() => {
    skipOnboarding();
    setIsOpen(false);
  }, [skipOnboarding]);

  return {
    preferences,
    questions,
    isOpen,
    isPersonalizeVisible,
    open,
    close,
    skip,
    complete: saveAndComplete,
  };
};

export default useOnboardingPresenter;
